import React, { useState } from 'react';
import { AwardIcon, ChevronDownIcon, CheckCircle2Icon, TrendingUpIcon, InfoIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
interface BusinessTiersProps {
  /** The tier the merchant currently sits at. */
  currentTier?: string;
  /** Optional intro line shown under the heading (e.g. on the top-up state). */
  intro?: string;
}
interface Tier {
  tier: string;
  threshold: number;
  /** Profit-rate discount conferred, e.g. 0.06. */
  discount: number;
  behaviors: string;
}
const TIERS: Tier[] = [{
  tier: 'Bronze',
  threshold: 0,
  discount: 0.0,
  behaviors: 'Open your first facility.'
}, {
  tier: 'Silver',
  threshold: 400,
  discount: 0.03,
  behaviors: 'Maintain healthy signals for roughly 3 months.'
}, {
  tier: 'Gold',
  threshold: 700,
  discount: 0.06,
  behaviors: 'Sustain on-time settlement and low chargebacks for around 5 months.'
}, {
  tier: 'Platinum',
  threshold: 1000,
  discount: 0.1,
  behaviors: 'Add a 90-day no-missed-settlement streak (+200) and a growth bonus (+350) on top of steady monthly activity.'
}];
// Illustrative monthly earning rates (sum to ~140 / month) and one-off bonuses.
const EARNING_RATES = [{
  label: 'On-time settlement',
  value: '+40 / mo'
}, {
  label: 'Chargebacks under 0.5%',
  value: '+30 / mo'
}, {
  label: 'Revenue growth maintained',
  value: '+30 / mo'
}, {
  label: 'Customer diversity',
  value: '+20 / mo'
}, {
  label: 'Active trading days',
  value: '+20 / mo'
}];
const EARNING_BONUSES = [{
  label: '90-day no-missed-settlement streak',
  value: '+200 one-off'
}, {
  label: 'Growth mode (sustained ramp)',
  value: '+350 one-off'
}];
// Recent points activity — authored to sum to exactly the displayed balance.
const POINTS_LEDGER = [{
  amount: 200,
  reason: '90-day no-missed-settlement streak',
  date: 'Apr 2026'
}, {
  amount: 40,
  reason: 'On-time settlement',
  date: 'May 2026'
}, {
  amount: 30,
  reason: 'Chargebacks under 0.5%',
  date: 'May 2026'
}, {
  amount: 30,
  reason: 'Revenue growth maintained',
  date: 'May 2026'
}, {
  amount: 20,
  reason: 'Customer diversity',
  date: 'May 2026'
}, {
  amount: 20,
  reason: 'Active trading days',
  date: 'May 2026'
}, {
  amount: 140,
  reason: 'Healthy signals (full month)',
  date: 'Apr 2026'
}, {
  amount: 140,
  reason: 'Healthy signals (full month)',
  date: 'Mar 2026'
}, {
  amount: 80,
  reason: 'Early-facility activity',
  date: 'Feb 2026'
}];
const TYPICAL_FACILITY = 170000;
const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
  currency: 'MYR'
});
export function BusinessTiers({
  currentTier = 'Gold',
  intro
}: BusinessTiersProps) {
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const earnedPoints = POINTS_LEDGER.reduce((sum, e) => sum + e.amount, 0);
  const current = TIERS.find(t => t.tier === currentTier) ?? TIERS[2];
  const nextTier = TIERS.find(t => t.threshold > earnedPoints);
  const pointsToNext = nextTier ? nextTier.threshold - earnedPoints : 0;
  const progress = nextTier ? Math.min(earnedPoints / nextTier.threshold * 100, 100) : 100;
  const exampleVsCurrent = (tier: Tier) => {
    const delta = (tier.discount - current.discount) * TYPICAL_FACILITY;
    if (Math.abs(delta) < 1) return `Your current tier — applied to your next facility.`;
    if (delta > 0) return `Saves ~${formatMYR(delta)} on a ${formatMYR(TYPICAL_FACILITY)} facility vs ${current.tier}.`;
    return `Costs ~${formatMYR(Math.abs(delta))} more than ${current.tier} on a ${formatMYR(TYPICAL_FACILITY)} facility.`;
  };
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3482" data-mp="1muv7zo24uo18">
      {/* Header */}
      <div className="p-8 border-b border-gray-100 flex flex-col md:flex-row md:items-start justify-between gap-6" data-id="element-3483" data-mp="3iwf5ngar5peg">
        <div data-id="element-3484" data-mp="3dywvjml0144v">
          <div className="flex items-center gap-2 mb-2" data-id="element-3485" data-mp="3dz6jz5qeb38g">
            <AwardIcon size={24} className="text-[var(--brand-primary)]" data-id="element-3486" data-mp="39w0isci5428u" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-3487" data-mp="34xz0htsjg6yf">
              {BRAND.name} Business Tiers
            </h2>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-3488" data-mp="2zznnxbauzici">
            Better business → Bigger loyalty discount
          </h3>
          <p className="text-gray-600 max-w-2xl" data-id="element-3489" data-mp="2v5szjukb6f73">
            {intro ?? 'You earn points while this facility is active. Your tier sets the loyalty discount applied to your next facility.'}
          </p>
        </div>
        <div className="bg-gray-50 px-6 py-4 rounded-xl border border-gray-200 min-w-[200px]" data-id="element-3490" data-mp="2h7uuxgvshx4s">
          <div className="flex items-center justify-between mb-1" data-id="element-3491" data-mp="2c9bbpo8kyzg4">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3492" data-mp="2cb4hl1bddfn2">
              Your points
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-[var(--brand-primary)] text-xs font-bold" data-id="element-3493" data-mp="wptxv0s7xu3i">
              {currentTier}
            </span>
          </div>
          <div className="text-3xl font-bold text-[var(--brand-primary)]" data-id="element-3494" data-mp="15qicnxpfryhg">
            {earnedPoints}
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mt-3" data-id="element-3495" data-mp="91aoehxfnb9u">
            <div className="h-full bg-[var(--brand-primary)]" style={{
            width: `${progress}%`
          }} data-id="element-3496" data-mp="dzcehmta0aut" />
          </div>
          {nextTier && <p className="text-xs text-gray-500 mt-2" data-id="element-3497" data-mp="ms5zm0vem4gl">
              {pointsToNext} points to {nextTier.tier}
            </p>}
        </div>
      </div>

      <div className="p-8" data-id="element-3498" data-mp="3mdl1mfd149fl">
        {/* Tier ladder — click a tier to explore */}
        <div className="flex items-center justify-between mb-6" data-id="element-3499" data-mp="3qf689iyyzrhc">
          <div className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-3500" data-mp="xlh89a5kgg30">
            Loyalty discount on your next facility
          </div>
          <span className="text-xs text-gray-400" data-id="element-3501" data-mp="sllbnvz77qxh">Tap a tier to explore</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3502" data-mp="17e52py92q7yk">
          {TIERS.map(t => {
          const isCurrent = t.tier === currentTier;
          const isSelected = t.tier === selectedTier;
          return <button key={t.tier} onClick={() => setSelectedTier(prev => prev === t.tier ? null : t.tier)} aria-expanded={isSelected} className={`relative p-5 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/20' : isCurrent ? 'border-[var(--brand-primary)] bg-red-50/30' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3503" data-mp="9otpnes9uxpq">
                {isCurrent && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[var(--brand-primary)] text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-sm" data-id="element-3504" data-mp="22k4ckoea5t64">
                    You
                  </div>}
                <span className={`font-bold ${isCurrent ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-3505" data-mp="1xljd4r6urdiq">
                  {t.tier}
                </span>
                <div className="text-xs text-gray-400 mt-0.5" data-id="element-3506" data-mp="2qaqhi6szmxbw">
                  {t.threshold.toLocaleString()}+ points
                </div>
                <div className={`text-2xl font-bold mt-3 ${isCurrent ? 'text-[var(--brand-primary)]' : 'text-gray-900'}`} data-id="element-3507" data-mp="2blko4zgu01bx">
                  −{t.discount.toFixed(2)}
                </div>
                <div className="flex items-center justify-between mt-1" data-id="element-3508" data-mp="348omwmpbzjf9">
                  <span className="text-xs text-gray-500" data-id="element-3509" data-mp="3932kmvrgu2n3">
                    Fixed-profit rate
                  </span>
                  <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${isSelected ? 'rotate-180' : ''}`} data-id="element-3510" data-mp="191crsmmcwkh4" />
                </div>
              </button>;
        })}
        </div>

        {/* Expanded tier explorer */}
        {selectedTier && (() => {
        const t = TIERS.find(x => x.tier === selectedTier)!;
        return <div className="mt-4 p-6 rounded-xl border border-gray-200 bg-gray-50" data-id="element-3511" data-mp="1nvzebog5lmub">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4" data-id="element-3512" data-mp="1ix6362oca7xb">
                  <h4 className="text-lg font-bold text-gray-900" data-id="element-3513" data-mp="1dyld2w2sx4ml">
                    {t.tier} tier
                  </h4>
                  <span className="text-sm text-gray-500" data-id="element-3514" data-mp="7ij1s9pq9dsh">
                    {t.threshold.toLocaleString()}+ points · −
                    {t.discount.toFixed(2)} fixed-profit rate
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-3515" data-mp="3om6sgog4xbmf">
                  <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3516" data-mp="3tkrrwlnkbr9t">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3517" data-mp="2d5i0ba92ski">
                      Example saving
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-3518" data-mp="75yp35t6yczr">
                      {exampleVsCurrent(t)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3519" data-mp="g3sio8zkmsj8">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3520" data-mp="l3n8e97m1c2p">
                      How to reach it
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-3521" data-mp="q0ndohwmyasr">{t.behaviors}</p>
                  </div>
                </div>

                {/* Points-earning rate table */}
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3522" data-mp="2ruwekpqw4xam">
                  How points are earned (illustrative)
                </div>
                <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100" data-id="element-3523" data-mp="20rrwjidvs5bf">
                  {EARNING_RATES.map(r => <div key={r.label} className="flex items-center justify-between px-4 py-2.5 text-sm" data-id="element-3524" data-mp="24whpayd8t3s4">
                      <span className="text-gray-700" data-id="element-3525" data-mp="2ert4fhskcd0e">{r.label}</span>
                      <span className="font-medium text-gray-900" data-id="element-3526" data-mp="2jqm7ega84lmb">
                        {r.value}
                      </span>
                    </div>)}
                  {EARNING_BONUSES.map(r => <div key={r.label} className="flex items-center justify-between px-4 py-2.5 text-sm bg-gray-50/60" data-id="element-3527" data-mp="2sj7ozt24cm9k">
                      <span className="text-gray-700" data-id="element-3528" data-mp="32iyhxqb16t7j">{r.label}</span>
                      <span className="font-medium text-[var(--brand-primary)]" data-id="element-3529" data-mp="3clkaiypro643">
                        {r.value}
                      </span>
                    </div>)}
                </div>
              </div>;
      })()}

        {/* Actionable path to the next tier */}
        {nextTier && <div className="mt-8 p-4 rounded-xl bg-red-50/50 border border-red-100" data-id="element-3530" data-mp="2yn44l9zqajn0">
            <p className="text-sm text-gray-700" data-id="element-3531" data-mp="3vcl9hmqw01nn">
              You're{' '}
              <strong className="text-gray-900" data-id="element-3532" data-mp="3qdifcxcg3fug">
                {pointsToNext} points from {nextTier.tier}
              </strong>{' '}
              (−{nextTier.discount.toFixed(2)} discount). At ~130 points/month,
              that's about 2–3 months of healthy activity — or earn it faster
              with a 90-day streak (+200) plus a growth bonus (+350).
            </p>
          </div>}

        {/* Recent points activity ledger */}
        <div className="mt-8" data-id="element-3533" data-mp="d55mfov5xfa6">
          <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3534" data-mp="d4vs5p334lyo">
            Recent points activity
          </div>
          <div className="rounded-xl border border-gray-200 divide-y divide-gray-100" data-id="element-3535" data-mp="2cdx5gwdn9n6n">
            {POINTS_LEDGER.map((e, i) => <div key={i} className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-3536" data-mp="1xohfhilxtls0" data-mpi={i}>
                <div className="flex items-center gap-3 min-w-0" data-id="element-3537" data-mp="1oock66u0qdu7">
                  <span className="inline-flex items-center justify-center min-w-[3rem] px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-sm font-bold" data-id="element-3538" data-mp="1omrscoz39901">
                    +{e.amount}
                  </span>
                  <span className="text-sm text-gray-700 truncate" data-id="element-3539" data-mp="1es9kd7mn2raf">
                    {e.reason}
                  </span>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap" data-id="element-3540" data-mp="394zd3m19tug8">
                  {e.date}
                </span>
              </div>)}
            <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-50" data-id="element-3541" data-mp="12fnll21mqhrk">
              <span className="text-sm font-semibold text-gray-900" data-id="element-3542" data-mp="12db56tbisfgm">
                Total points balance
              </span>
              <span className="text-sm font-bold text-[var(--brand-primary)]" data-id="element-3543" data-mp="1c5z35re8kpgx">
                {earnedPoints}
              </span>
            </div>
          </div>
        </div>

        {/* How you earn points (behaviors) */}
        <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-3544" data-mp="1l53x0keurumr">
          <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3545" data-mp="1h4tuxiu6lod6">
            How you earn points
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3546" data-mp="1v309bfjj0vmg">
            {['On-time settlement, no missed deductions', 'Low chargeback rate (under 0.5%)', 'Steady or growing monthly revenue', 'A diverse, non-concentrated customer base'].map(item => <li key={item} className="flex items-center gap-2.5" data-id="element-3547" data-mp="3rypo9mz6u6by">
                <CheckCircle2Icon size={18} className="text-[var(--brand-primary)] flex-shrink-0" data-id="element-3548" data-mp="3tcz1v3nvyw1f" />
                <span className="text-sm text-gray-700" data-id="element-3549" data-mp="76kbtqpzqmz5">{item}</span>
              </li>)}
          </ul>
        </div>

        {/* Keeping your tier — retention explainer (both directions) */}
        <div className="mt-8 p-5 rounded-xl bg-gray-50 border border-gray-100 flex items-start gap-3" data-id="element-3550" data-mp="3anakkdcyje4j">
          <InfoIcon size={18} className="text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3551" data-mp="3fm40sdjmpj8e" />
          <div data-id="element-3552" data-mp="3fk10n0ori9py">
            <div className="text-sm font-bold text-gray-900 mb-1" data-id="element-3553" data-mp="3klqgctmxjbkd">
              Keeping your tier
            </div>
            <p className="text-sm text-gray-600" data-id="element-3554" data-mp="3pcf6a4iyo0he">
              Your tier is reassessed when you take your next facility, based on
              points earned during your current one. Maintaining on-time
              settlement and low chargebacks keeps you at {currentTier} or
              higher; a sustained drop in activity can lower your tier. All
              point figures and thresholds are illustrative.
            </p>
          </div>
        </div>
      </div>
    </div>;
}